import { z } from 'zod'
import { zColor } from '@remotion/zod-types'
import { LAYOUT_KEYS } from './layout'

// Orientation schema
export const orientationSchema = z.enum([
  'square',
  'landscape',
  'portrait',
] as const)

// Speaker schema
export const speakerSchema = z.object({
  avatarUrl: z.string().url().nullable().optional(),
  name: z.string().nullable().optional(),
  username: z.string().nullable().optional(),
  role: z.enum(['HOST', 'COHOST', 'SPEAKER']).nullable().optional(),
  speakingTime: z.number().optional(),
})

// Episode schema
export const episodeSchema = z.object({
  duration: z.number().optional(),
  image: z.string().nullable().optional(),
  logo: z.boolean().optional(),
  startedAt: z.string().optional(),
  subtitle: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  url: z.string().url().optional(),
})

// ThemeConfig schema
export const themeConfigSchema = z.object({
  colors: z.object({
    primary: zColor(),
    secondary: zColor(),
    accent: zColor(),
  }),
  fonts: z.object({
    primary: z.string(),
  }),
})

// TextStyle schema
export const textStyleSchema = z.object({
  color: zColor(),
  fontFamily: z.string(),
  opacity: z.number().optional(),
})

// TranscriptionStyle schema
export const transcriptionStyleSchema = textStyleSchema.extend({
  background: z.string().optional(),
  highlightColor: zColor(),
  highlightBgColor: z.string().optional(),
  highlightType: z.enum(['last-word', 'all-words', 'last-word-bg']),
  unPlayedColor: z.string().optional(),
  unPlayedOpacity: z.number().optional(),
  fontSize: z.number(),
  fontWeight: z.number(),
  fontStyle: z.enum(['normal', 'italic']).optional(),
  letterSpacing: z.number().optional(),
  linesPerPage: z.number(),
  lineHeight: z.number(),
  textAlign: z.enum(['center', 'left', 'right']),
  scrollType: z.enum(['line-by-line', 'page-by-page']),
  textStrokeColor: z.string().optional(),
  textStrokeWidth: z.number().optional(),
  textTransform: z.enum(['uppercase', 'lowercase', 'capitalize']).optional(),
})

// BackgroundStyle schema
export const backgroundStyleSchema = z.object({
  color: z.string().optional(),
  image: z.string().optional(),
})

// Style schema
export const styleSchema = z.object({
  background: backgroundStyleSchema,
  title: textStyleSchema,
  subtitle: textStyleSchema,
  speakerUsername: textStyleSchema,
  speakerName: textStyleSchema.optional(),
  logo: z.object({ color: zColor() }),
  date: textStyleSchema.optional(),
  border: z.object({ color: zColor() }),
  transcription: transcriptionStyleSchema,
  waveform: z.object({ color: zColor() }),
})

// Transition schema
export const transitionSchema = z.object({
  type: z.string().optional(),
  duration: z.number(),
})

// Word schema
export const wordSchema = z.object({
  end: z.number(),
  start: z.number(),
  text: z.string(),
})

// Paragraph schema
export const paragraphSchema = z.object({
  end: z.number(),
  start: z.number(),
  speaker: speakerSchema,
})

// SceneConfig schema (discriminated union)
const baseSceneConfigSchema = z.object({
  kind: z.enum([
    'clip',
    'qr',
    'keywords',
    'reactions',
    'speaking',
    'title',
  ] as const),
  duration: z.number(),
  transition: transitionSchema.optional(),
})

const clipSceneConfigSchemaBase = baseSceneConfigSchema.extend({
  kind: z.literal('clip'),
  layout: z.enum(LAYOUT_KEYS),
  mediaUrl: z.string().url(),
  /**
   * The audio-only rendition to run the FFT against, when one is known to
   * exist. Absent means "analyze `mediaUrl`" — see `sceneAudioSrc`.
   */
  audioUrl: z.string().url().nullable().optional(),
  /**
   * `waveform.dat` for the analyzed media, used for the whole-file amplitude
   * peak the windowed FFT rescales against — see `frequencyScale.ts`.
   */
  waveformUrl: z.string().url().nullable().optional(),
  mediaStart: z.number().optional(),
  /**
   * The kept pieces of the media, in play order (#89): each plays
   * `[mediaStart, mediaStart + duration]` of the file, back to back, so the
   * scene skips whatever lies between them. Media-relative, like
   * `mediaStart`. Absent means one continuous piece from `mediaStart` — every
   * scene before #89, and every render with no cuts. Captions already arrive
   * on the cut timeline. See `utils/mediaTimeline.ts`.
   */
  segments: z
    .array(z.object({ mediaStart: z.number(), duration: z.number() }))
    .optional(),
  paragraphs: z.array(paragraphSchema),
  style: styleSchema.optional(),
  theme: themeConfigSchema,
  title: z.string().nullable().optional(),
  subtitle: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  showZealousLogo: z.boolean().optional(),
  words: z.array(wordSchema),
})

const qrSceneConfigSchema = baseSceneConfigSchema.extend({
  kind: z.literal('qr'),
})

const keywordsSceneConfigSchema = baseSceneConfigSchema.extend({
  kind: z.literal('keywords'),
})

const reactionsSceneConfigSchema = baseSceneConfigSchema.extend({
  kind: z.literal('reactions'),
})

const speakingSceneConfigSchema = baseSceneConfigSchema.extend({
  kind: z.literal('speaking'),
})

const titleSceneConfigSchema = baseSceneConfigSchema.extend({
  kind: z.literal('title'),
})

export const sceneConfigSchema = z.discriminatedUnion('kind', [
  clipSceneConfigSchemaBase,
  qrSceneConfigSchema,
  keywordsSceneConfigSchema,
  reactionsSceneConfigSchema,
  speakingSceneConfigSchema,
  titleSceneConfigSchema,
])

// ClipSceneConfig schema
export const clipSceneConfigSchema = clipSceneConfigSchemaBase

// AudiogramConfig schema
export const audiogramConfigSchema = z.object({
  episode: episodeSchema,
  reactions: z
    .array(
      z.object({
        emoji: z.string(),
        count: z.number(),
      }),
    )
    .optional(),
  scenes: z.array(sceneConfigSchema).optional(),
  speakers: z.array(speakerSchema).optional(),
  theme: themeConfigSchema,
  wordMap: z
    .array(
      z.object({
        word: z.string(),
        count: z.number(),
      }),
    )
    .optional(),
})

// Component prop schemas
export const audiogramPropsSchema = z.object({
  config: audiogramConfigSchema,
  orientation: orientationSchema,
})

export const clipScenePropsSchema = z.object({
  clipScene: clipSceneConfigSchema,
  orientation: orientationSchema,
})

export const scenePropsSchema = z.object({
  audiogram: audiogramConfigSchema,
  orientation: orientationSchema,
  scene: sceneConfigSchema,
})
